import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { SubscriptionChart } from "@/components/dashboard/SubscriptionChart";
import {
  ActivationsTable,
  type Activation,
} from "@/components/dashboard/ActivationsTable";
import { RefreshButton } from "@/components/dashboard/RefreshButton";
import { formatCompactCurrency } from "@/lib/utils";

type MetricType = "clients" | "mrr" | "agencies";

type Metrics = {
  clients: { current: number; delta: number };
  mrr: { currentCents: number; deltaPct: number | null };
  agencies: { current: number; delta: number };
  updatedAt: string | null;
  hasData: boolean;
};

function formatAbsDelta(value: number): {
  value: string;
  positive: boolean | null;
} {
  if (value === 0) return { value: "0", positive: null };
  return {
    value: value > 0 ? `+${value}` : `${value}`,
    positive: value > 0,
  };
}

function formatPctDelta(value: number | null): {
  value: string;
  positive: boolean | null;
} | null {
  if (value === null) return null;
  if (value === 0) return { value: "0%", positive: null };
  const prefix = value > 0 ? "+" : "";
  return {
    value: `${prefix}${value.toFixed(1)}%`,
    positive: value > 0,
  };
}

export function DashboardPage() {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [activations, setActivations] = useState<Activation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMetric, setActiveMetric] = useState<MetricType | null>("clients");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mRes, aRes] = await Promise.all([
        fetch("/api/stripe/metrics", { credentials: "include" }),
        fetch("/api/stripe/activations", { credentials: "include" }),
      ]);
      if (!mRes.ok) throw new Error(`Metrics request failed (${mRes.status})`);
      if (!aRes.ok)
        throw new Error(`Activations request failed (${aRes.status})`);
      const m = (await mRes.json()) as Metrics;
      const a = (await aRes.json()) as { activations: Activation[] };
      setMetrics(m);
      setActivations(a.activations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clients = metrics?.clients.current ?? 0;
  const mrrCents = metrics?.mrr.currentCents ?? 0;
  const agencies = metrics?.agencies.current ?? 0;
  const hasPrior = Boolean(metrics && metrics.hasData);

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {metrics?.updatedAt
              ? `Last synced ${new Date(metrics.updatedAt).toLocaleString()}`
              : "No sync data yet"}
          </p>
        </div>
        <RefreshButton onRefreshed={load} />
      </div>

      {error && (
        <div className="mb-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load data: {error}
        </div>
      )}

      {loading && !metrics ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <MetricCard
              label="Clients"
              value={clients.toLocaleString()}
              delta={
                hasPrior ? formatAbsDelta(metrics?.clients.delta ?? 0) : null
              }
              active={activeMetric === "clients"}
              onClick={() =>
                setActiveMetric((m) => (m === "clients" ? null : "clients"))
              }
            />
            <MetricCard
              label="MRR"
              value={formatCompactCurrency(mrrCents)}
              delta={formatPctDelta(metrics?.mrr.deltaPct ?? null)}
              active={activeMetric === "mrr"}
              onClick={() =>
                setActiveMetric((m) => (m === "mrr" ? null : "mrr"))
              }
            />
            <MetricCard
              label="Agencies"
              value={agencies.toLocaleString()}
              delta={
                hasPrior
                  ? formatAbsDelta(metrics?.agencies.delta ?? 0)
                  : null
              }
              active={activeMetric === "agencies"}
              onClick={() =>
                setActiveMetric((m) => (m === "agencies" ? null : "agencies"))
              }
            />
          </div>

          {activeMetric && (
            <div className="mb-8">
              <SubscriptionChart metric={activeMetric} />
            </div>
          )}

          <ActivationsTable activations={activations} />
        </>
      )}
    </div>
  );
}
